// Estudiante — vista de "Mi información" con datos básicos y resumen visual.
// Refresca en cada montaje el conteo de cuestionarios pendientes / completados
// para evitar mostrar estado en caché tras completar una evaluación.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  User as UserIcon, Mail, GraduationCap, CalendarDays, Sparkles,
  BookOpenCheck, CheckCircle2, AlertCircle, ChevronRight, RefreshCw, Rocket, ClipboardCheck,
} from "lucide-react";

export default function EstudiantePerfil() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [evalsPending, setEvalsPending] = useState(0);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/questionnaire-assignments/me");
      setAssignments(Array.isArray(data) ? data : []);
    } catch {
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);
  // v5.4.0 · docente acompañante, % del curso y evaluaciones de clase pendientes
  useEffect(() => {
    api.get("/student-extras/me").then(({ data }) => setSummary(data)).catch(() => setSummary(null));
    api.get("/student-extras/class-evals/mine")
      .then(({ data }) => setEvalsPending((data.items || []).filter((i) => i.status === "pending").length))
      .catch(() => setEvalsPending(0));
  }, []);

  if (!user) return null;

  const pending = assignments.filter((a) => a.status === "pending" || a.status === "in_progress");
  const completed = assignments.filter((a) => a.status === "completed");
  const cep1Done = completed.some((a) => (a.template_code || "").toUpperCase() === "CEP1");

  return (
    <div className="space-y-6" data-testid="estudiante-perfil-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#D63384]">Perfil</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">Mi información</h1>
          <p className="text-sm text-[#6E7591] mt-1">Tu identidad JASQ y datos básicos en la plataforma.</p>
        </div>
        <button
          onClick={reload}
          disabled={loading}
          data-testid="perfil-refresh"
          className="jasq-btn-ghost text-xs inline-flex items-center gap-1.5"
          title="Volver a consultar tus diagnósticos"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      <div className="jasq-card p-6 flex flex-col md:flex-row items-center gap-6">
        <img src={user.avatar} alt={user.name} className="w-32 h-32 rounded-2xl border-4 border-[#FFCC28] object-cover bg-white shadow-lg" data-testid="perfil-avatar" />
        <div className="flex-1 space-y-2 text-center md:text-left">
          <h2 className="font-display text-2xl font-extrabold text-[#1F2547]">{user.name}</h2>
          <p className="text-sm text-[#6E7591] inline-flex items-center gap-2"><Mail size={14} /> {user.email}</p>
          <div className="flex flex-wrap gap-2 mt-2 justify-center md:justify-start">
            <span className="jasq-pill bg-[#FFE9F0] text-[#A52258]"><Sparkles size={12} /> Estudiante JASQ®</span>
            {user.age && <span className="jasq-pill bg-[#FFF7DA] text-[#9C7A18]"><CalendarDays size={12} /> {user.age} años</span>}
            {user.grade && <span className="jasq-pill bg-[#E8EEFB] text-[#2C49A0]"><GraduationCap size={12} /> {user.grade}</span>}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card
          testid="card-docente"
          title="Docente acompañante"
          body={summary?.teacher?.name || (user.teacher_id ? "Cargando…" : "Tu docente acompañante se asignará pronto.")}
          icon={UserIcon}
        />
        <Card
          testid="card-cep1"
          title="Diagnóstico inicial"
          body={cep1Done
            ? "¡Listo! Tu diagnóstico CEP1 está completo."
            : "Aún tienes el CEP1 pendiente."}
          icon={cep1Done ? CheckCircle2 : AlertCircle}
          tone={cep1Done ? "ok" : "warn"}
        />
      </div>

      {summary && (
        <div className="jasq-card p-5" data-testid="card-curso">
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790] inline-flex items-center gap-1">
            <Rocket size={12} /> Avance de mi curso
          </p>
          <div className="flex items-end justify-between mt-1">
            <h3 className="font-display text-3xl font-extrabold text-[#1F2547]" data-testid="curso-percent">{summary.course.percent}%</h3>
            <p className="text-xs text-[#6E7591]">
              {summary.course.classes_done} de {summary.course.classes_total} clases tomadas
              {summary.course.classes_scheduled > 0 ? ` · ${summary.course.classes_scheduled} por venir` : ""}
            </p>
          </div>
          <div className="h-3 rounded-full bg-[#E1E5F0] overflow-hidden mt-2">
            <div className="h-full rounded-full bg-gradient-to-r from-[#A43790] to-[#FFCC28]" style={{ width: `${summary.course.percent}%` }} />
          </div>
          {summary.ruta && (
            <p className="text-xs text-[#6E7591] mt-3" data-testid="curso-mundos">
              Mundos de tu ruta: <b className="text-[#1F2547]">{summary.ruta.mundos_completados} de {summary.ruta.mundos_total}</b> conquistados ({summary.ruta.percent}%)
            </p>
          )}
        </div>
      )}

      {evalsPending > 0 && (
        <div className="jasq-card p-5 flex items-center justify-between gap-3 flex-wrap border border-[#FFCC28]/70 bg-[#FFF7DA]" data-testid="card-evals-pendientes">
          <p className="text-sm font-bold text-[#1F2547] inline-flex items-center gap-2"><ClipboardCheck size={16} className="text-[#A43790]" /> Tienes {evalsPending} evaluación{evalsPending === 1 ? "" : "es"} de clase por responder</p>
          <Link to="/estudiante/evaluacion-clase" className="jasq-btn-primary text-sm inline-flex items-center gap-2">Responder <ChevronRight size={14} /></Link>
        </div>
      )}

      <div className="jasq-card p-5" data-testid="card-mis-evaluaciones">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790] inline-flex items-center gap-1">
              <BookOpenCheck size={12} /> Mis diagnósticos
            </p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547] mt-1">
              {loading
                ? "Cargando…"
                : pending.length === 0
                  ? "No tienes diagnósticos pendientes 🎉"
                  : `Tienes ${pending.length} diagnóstico${pending.length === 1 ? "" : "s"} por hacer`}
            </h3>
            <p className="text-xs text-[#6E7591] mt-1">
              {completed.length} terminado(s) · actualizado al instante
            </p>
          </div>
          <Link
            to="/estudiante/cuestionarios"
            data-testid="link-ir-evaluaciones"
            className="jasq-btn-primary inline-flex items-center gap-2 text-sm"
          >
            Ir a mis diagnósticos <ChevronRight size={14} />
          </Link>
        </div>

        {pending.length > 0 && (
          <ul className="mt-4 divide-y divide-[#E1E5F0]" data-testid="pending-list">
            {pending.slice(0, 5).map((a) => (
              <li key={a.id} className="py-2.5 flex items-center gap-3 text-sm">
                <span className="text-[10px] uppercase tracking-wider font-bold bg-[#FFF7DA] text-[#9C7A18] rounded-full px-2 py-0.5">
                  {a.template_code}
                </span>
                <span className="flex-1 font-bold text-[#1F2547] truncate">{a.template_title}</span>
                <Link
                  to={`/estudiante/cuestionario/${a.id}`}
                  data-testid={`pending-go-${a.id}`}
                  className="text-xs font-extrabold text-[#A43790] hover:underline inline-flex items-center gap-1"
                >
                  Iniciar <ChevronRight size={12} />
                </Link>
              </li>
            ))}
            {pending.length > 5 && (
              <li className="py-2 text-[11px] text-[#6E7591] text-center">
                +{pending.length - 5} más en la lista completa
              </li>
            )}
          </ul>
        )}
      </div>

    </div>
  );
}

function Card({ title, body, icon: Icon = Sparkles, tone, testid }) {
  const toneStyles = tone === "ok"
    ? "border-[#3FA84D]/60 bg-[#F4FBEF]"
    : tone === "warn"
      ? "border-[#F5B82E]/60 bg-[#FFF7DA]"
      : "";
  return (
    <div className={`jasq-card p-5 ${toneStyles}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790] inline-flex items-center gap-1">
        <Icon size={12} /> {title}
      </p>
      <p className="text-sm text-[#1F2547] font-bold mt-1 leading-relaxed">{body}</p>
    </div>
  );
}
